import cx from 'classix'
import { graphql, useStaticQuery } from 'gatsby'
import React from 'react'

import i18n from '@src/config/i18n'
import locales from '@src/config/locales'

const replaceTrailing = (_path) => _path.replace(/\/$/, ``)

export type BilingualValueType = {
  fr?: string
  en?: string
}

type HeadProps = {
  article?: boolean
  course?: boolean
  data?: any
  description?: string
  home?: boolean
  image?: string | null
  itemNode?: any
  lang?: string
  noIndex?: boolean
  pathname: string
  paths?: BilingualValueType
  title: string
  htmlClass?: string
}

export const Head = ({
  article = false,
  course = false,
  data = null,
  description = ``,
  home = false,
  image = ``,
  itemNode = null,
  lang = i18n.language,
  noIndex = false,
  pathname,
  paths = {},
  title,
  htmlClass,
}: HeadProps) => {
  const { buildTime, config } = data.site

  const locale = locales[lang]
  const localizedPath = `/${locale.path}`
  const homeURL = `${config.siteUrl}${localizedPath}`
  const URL = `${config.siteUrl}${replaceTrailing(pathname)}`
  const alternateLocale = lang === `en` ? `fr` : `en`
  const alternateURL =
    paths && paths[alternateLocale] ? `${config.siteUrl}${paths[alternateLocale]}` : null

  const shareImage = image ? image : `${config.siteBanner}${locale.path}.jpg`
  description = description || config.siteDescription[lang]

  const orgaCreator = (input) => ({
    '@context': `http://schema.org`,
    '@id': `${config.siteUrl}/#${input}`,
    '@type': `Organization`,
    address: {
      '@type': `PostalAddress`,
      addressCountry: `CA`,
      addressLocality: `Quebec`,
      postalCode: `G1M 0A2`,
    },
    name: config.siteTitle,
    alternateName: config.siteShortName,
    description: config.siteDescription[lang],
    url: homeURL,
    email: `info@afiexpertise.com`,
    image: {
      '@type': `ImageObject`,
      url: `${config.siteLogo}${locale.path}.png`,
      height: `512`,
      width: `512`,
    },
    logo: {
      '@type': `ImageObject`,
      url: `${config.siteLogoSmall}${locale.path}.png`,
      height: `60`,
      width: `60`,
    },
  })

  const searchPath = lang === `en` ? `/en/search/` : `/fr/recherche/`
  const schemaOrgWebSite = {
    '@context': `http://schema.org`,
    '@type': `WebSite`,
    url: `${config.siteUrl}/${lang}`,
    potentialAction: {
      '@type': `SearchAction`,
      target: `${config.siteUrl}${searchPath}?q={search_term_string}`,
      'query-input': `required name=search_term_string`,
    },
  }

  const schemaOrgWebPage = {
    '@context': `http://schema.org`,
    '@type': `WebPage`,
    url: URL,
    headline: config.siteTagline[lang],
    inLanguage: locale.htmlLang,
    mainEntityOfPage: URL,
    description: config.siteDescription[lang],
    name: config.siteTitle,
    author: {
      '@id': `${config.siteUrl}/#identity`,
    },
    copyrightHolder: {
      '@id': `${config.siteUrl}/#identity`,
    },
    copyrightYear: `2019`,
    creator: {
      '@id': `${config.siteUrl}/#creator`,
    },
    publisher: {
      '@id': `${config.siteUrl}/#creator`,
    },
    datePublished: `2019-06-12`,
    dateModified: buildTime,
    image: {
      '@type': `ImageObject`,
      url: shareImage,
    },
  }

  const itemListElement: Record<string, any> = []

  let schemaCourse = {}
  if (course) {
    schemaCourse = {
      '@context': `http://schema.org`,
      '@type': `Course`,
      inLanguage: locale.htmlLang,
      name: title,
      headline: title,
      description,
      url: URL,
      mainEntityOfPage: URL,
      hasCourseInstance: itemNode.course.Sessions.map((session) => {
        const courseMode = [`onsite`]
        if (itemNode.course.IsVirtual) courseMode.push(`virtual`)
        return {
          '@type': `CourseInstance`,
          name: title,
          description,
          location: {
            '@type': `City`,
            name: session.City,
            address: {
              '@type': `PostalAddress`,
              addressCountry: `CA`,
              addressLocality: session.City,
            },
          },
          startDate: session.Dates[0],
          endDate: session.Dates[session.Dates.length - 1],
          inLanguage: session.Language,
          courseMode,
        }
      }),
    }
    // insert on breadcrumb
    itemListElement.push({
      '@type': `ListItem`,
      item: {
        '@id':
          locale.htmlLang === `en`
            ? `${config.siteUrl}/en/training/catalog`
            : `${config.siteUrl}/fr/formation/catalogue`,
        name: locale.htmlLang === `en` ? `Catalog` : `Catalogue`,
      },
      position: 1,
    })
    itemListElement.push({
      '@type': `ListItem`,
      item: {
        '@id': URL,
        name: title,
      },
      position: 2,
    })
  }

  let schemaArticle = {}
  if (article) {
    schemaArticle = {
      '@context': `http://schema.org`,
      '@type': `Article`,
      author: {
        '@id': `${config.siteUrl}/#identity`,
      },
      copyrightHolder: {
        '@id': `${config.siteUrl}/#identity`,
      },
      copyrightYear: itemNode.publication.publishedAt.substring(0, 4),
      creator: {
        '@id': `${config.siteUrl}/#creator`,
      },
      publisher: {
        '@id': `${config.siteUrl}/#creator`,
      },
      datePublished: itemNode.publication.publishedAt,
      dateModified: itemNode.publication.publishedAt,
      description,
      headline: title,
      inLanguage: locale.htmlLang,
      url: URL,
      name: title,
      image: {
        '@type': `ImageObject`,
        url: shareImage,
      },
      mainEntityOfPage: URL,
    }
    itemListElement.push({
      '@type': `ListItem`,
      item: {
        '@id':
          locale.htmlLang === `en`
            ? `${config.siteUrl}/en/resources/publications`
            : `${config.siteUrl}/fr/ressources/publications`,
        name: `Publications`,
      },
      position: 1,
    })
    itemListElement.push({
      '@type': `ListItem`,
      item: {
        '@id': URL,
        name: title,
      },
      position: 2,
    })
  }

  if (!course && !article) {
    itemListElement.push({
      '@type': `ListItem`,
      item: {
        '@id': homeURL,
        name: locale.htmlLang === `en` ? `Homepage` : `Accueil`,
      },
      position: 1,
    })
    itemListElement.push({
      '@type': `ListItem`,
      item: {
        '@id': URL,
        name: title,
      },
      position: 2,
    })
  }

  const breadcrumb = {
    '@context': `http://schema.org`,
    '@type': `BreadcrumbList`,
    description: `Breadcrumbs list`,
    name: `Breadcrumbs`,
    itemListElement,
  }

  return (
    <head>
      <title>{`${title} | ${config.siteTitle}`}</title>
      <html lang={locale.htmlLang} className={cx(htmlClass)} />
      <link rel='canonical' href={URL} />
      <link rel='alternate' hrefLang={lang} href={URL} />
      {alternateURL && <link rel='alternate' hrefLang={alternateLocale} href={alternateURL} />}
      <meta httpEquiv='content-language' content={lang} />
      <meta name='description' content={description} />
      <meta name='image' content={shareImage} />

      {/*
      <link rel='apple-touch-icon' href='/favicons/apple-touch-icon.png' />
      <link rel='icon' type='image/png' sizes='32x32' href='/favicons/favicon-32x32.png' />
      <link rel='icon' type='image/png' sizes='16x16' href='/favicons/favicon-16x16.png' />
      <link rel='mask-icon' href='/favicons/safari-pinned-tab.svg' color='#343849' />
      <meta name='msapplication-TileColor' content='#3498db' />
      */}

      <meta property='og:locale' content={locale.ogLang} />
      <meta property='og:site_name' content={config.siteTitle} />
      <meta property='og:url' content={URL} />
      <meta property='og:type' content={article ? `article` : `website`} />
      <meta property='og:title' content={title} />
      <meta property='og:description' content={description} />
      <meta property='og:image' content={shareImage} />
      <meta property='og:image:alt' content={description} />

      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:creator' content={config.siteTitle} />
      <meta name='twitter:title' content={title} />
      <meta name='twitter:description' content={description} />
      <meta name='twitter:image' content={shareImage} />
      <meta name='twitter:image:alt' content={description} />

      <meta name='google-site-verification' content='aKKBL877apqYf8ZdqMo8ZchKX4jIdKPzU5Foxb587N0' />

      <meta name='robots' content={noIndex ? `noindex, nofollow` : `index, follow`} />

      {/* <link type='text/plain' href={`${config.siteUrl}/humans.txt`} rel='author' /> */}

      {article && <script type='application/ld+json'>{JSON.stringify(schemaArticle)}</script>}
      {course && <script type='application/ld+json'>{JSON.stringify(schemaCourse)}</script>}
      {!article && !course && (
        <script type='application/ld+json'>{JSON.stringify(schemaOrgWebPage)}</script>
      )}

      {home && <script type='application/ld+json'>{JSON.stringify(schemaOrgWebSite)}</script>}
      <script type='application/ld+json'>{JSON.stringify(breadcrumb)}</script>
      <script type='application/ld+json'>{JSON.stringify(orgaCreator(`identity`))}</script>
    </head>
  )
}

const querySEO = graphql`
  query SEO {
    site {
      buildTime(formatString: "YYYY-MM-DD")
      config: siteMetadata {
        siteTitle
        siteShortName
        siteDescription {
          fr
          en
        }
        siteTagline {
          fr
          en
        }
        siteUrl
        siteLogo
        siteLogoSmall
        siteBanner
        twitter
        facebook
        linkedin
      }
    }
  }
`

const SEO = (props: HeadProps) => {
  const data = useStaticQuery(querySEO)

  return <Head {...props} data={data} />
}

export default SEO
